import type { ReactNode } from 'react';
import type { UserGuideSection } from './types';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';

const imageModules = import.meta.glob<string>('./content/images/*', {
  eager: true,
  import: 'default',
});

function resolveImage(src: string): string | undefined {
  const filename = src.replace(/^images\//, '');
  return imageModules[`./content/images/${filename}`];
}

function renderInlineText(text: string): ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function renderMarkdown(markdown: string): ReactNode[] {
  const lines = markdown.split('\n');
  const nodes: ReactNode[] = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index].trim();
    if (!line) {
      index += 1;
      continue;
    }

    if (line.startsWith('##### ')) {
      nodes.push(
        <h4 key={index} className="mt-3 text-base font-semibold">
          {line.replace(/^#####\s+/, '')}
        </h4>,
      );
      index += 1;
      continue;
    }

    const imageMatch = /^!\[([^\]]*)\]\(([^)]+)\)$/.exec(line);
    if (imageMatch) {
      const [, alt, src] = imageMatch;
      const resolvedSrc = resolveImage(src);
      nodes.push(
        <figure key={index} className="space-y-2">
          {resolvedSrc ? (
            <img
              src={resolvedSrc}
              alt={alt}
              className="mx-auto block h-auto max-w-full rounded-md border"
            />
          ) : null}
          {alt ? <figcaption className="text-center text-xs text-muted-foreground">{alt}</figcaption> : null}
        </figure>,
      );
      index += 1;
      continue;
    }

    if (line.startsWith('> Note:')) {
      const text = line.replace(/^>\s*Note:\s*/i, '');
      nodes.push(
        <div
          key={index}
          className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-foreground/90"
        >
          <span className="font-semibold">Note: </span>
          {text}
        </div>,
      );
      index += 1;
      continue;
    }

    if (/^\d+\.\s+/.test(line) || /^-\s+/.test(line)) {
      const ordered = /^\d+\.\s+/.test(line);
      const items: string[] = [];
      while (index < lines.length) {
        const itemLine = lines[index].trim();
        const match = ordered
          ? /^\d+\.\s+(.+)$/.exec(itemLine)
          : /^-\s+(.+)$/.exec(itemLine);
        if (!match) break;
        items.push(match[1]);
        index += 1;
      }
      const ListTag = ordered ? 'ol' : 'ul';
      const listClass = ordered
        ? 'list-decimal list-inside space-y-1 text-sm text-foreground/90 pl-2'
        : 'list-disc list-inside space-y-1 text-sm text-foreground/90 pl-2';
      nodes.push(
        <ListTag key={`list-${index}`} className={listClass}>
          {items.map((item, i) => (
            <li key={i} className="leading-relaxed">
              {renderInlineText(item)}
            </li>
          ))}
        </ListTag>,
      );
      continue;
    }

    if (line.startsWith('|')) {
      const headerLine = line;
      const separatorLine = lines[index + 1]?.trim() ?? '';
      if (separatorLine.startsWith('|') && /^\|(?:\s*:?-+:?\s*\|)+\s*$/.test(separatorLine)) {
        const splitCells = (row: string) =>
          row
            .split('|')
            .slice(1, -1)
            .map((cell) => cell.trim());
        const headers = splitCells(headerLine);
        const rows: string[][] = [];
        index += 2;
        while (index < lines.length && lines[index].trim().startsWith('|')) {
          rows.push(splitCells(lines[index].trim()));
          index += 1;
        }
        nodes.push(
          <div key={`table-${index}`} className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  {headers.map((header) => (
                    <TableHead key={header} className="text-xs font-semibold whitespace-nowrap">
                      {header}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <TableCell key={cellIndex} className="text-xs align-top">
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>,
        );
        continue;
      }
    }

    const paragraph: string[] = [line];
    index += 1;
    while (index < lines.length) {
      const nextLine = lines[index].trim();
      if (
        !nextLine ||
        nextLine.startsWith('##### ') ||
        /^!\[[^\]]*\]\([^)]+\)$/.test(nextLine) ||
        nextLine.startsWith('> Note:') ||
        /^\d+\.\s+/.test(nextLine) ||
        /^-\s+/.test(nextLine) ||
        nextLine.startsWith('|')
      ) {
        break;
      }
      paragraph.push(nextLine);
      index += 1;
    }

    nodes.push(
      <p key={`p-${index}`} className="text-sm leading-relaxed text-foreground/90">
        {renderInlineText(paragraph.join(' '))}
      </p>,
    );
  }

  return nodes;
}

function UserGuideSectionView({ section, depth = 0 }: { section: UserGuideSection; depth?: number }) {
  const HeadingTag = depth === 0 ? 'h2' : depth === 1 ? 'h3' : 'h4';
  const headingClass =
    depth === 0
      ? 'text-2xl font-bold scroll-mt-24'
      : depth === 1
        ? 'text-xl font-semibold scroll-mt-24'
        : 'text-lg font-semibold scroll-mt-24';
  const title = section.number ? `${section.number} ${section.title}` : section.title;
  const showHeading = section.content.length > 0 || section.subsections?.length;

  return (
    <section id={section.id} className="space-y-4">
      {showHeading ? <HeadingTag className={headingClass}>{title}</HeadingTag> : null}
      {section.content ? <div className="space-y-3">{renderMarkdown(section.content)}</div> : null}
      {section.subsections?.map((subsection) => (
        <UserGuideSectionView key={subsection.id} section={subsection} depth={depth + 1} />
      ))}
    </section>
  );
}

export function UserGuideContent({ sections }: { sections: UserGuideSection[] }) {
  return (
    <>
      {sections.map((section) => (
        <UserGuideSectionView key={section.id} section={section} />
      ))}
    </>
  );
}
